// Consent management aligned with LSSI-CE art. 22.2, RGPD art. 4.11/7,
// EDPB Guidelines 03/2022 and AEPD "Guía sobre el uso de cookies" (nov 2023).
//
// Cómo añadir una nueva categoría o proveedor:
//   1. Añade la clave a `CATEGORIES` con su etiqueta y descripción.
//   2. En el punto donde cargas el script/iframe usa `hasConsent("categoria")`.
//   3. Sube `CONSENT_VERSION` para forzar re-consentimiento de todos los usuarios.
//
// El consentimiento caduca a los 24 meses (AEPD).

import { useEffect, useState, useSyncExternalStore } from "react";

export const CONSENT_VERSION = 1;
export const CONSENT_STORAGE_KEY = "cookie-consent-v1";
const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30 * 24; // 24 meses

export type ConsentCategory = "necessary" | "preferences" | "analytics" | "marketing";

export const CATEGORIES: Array<{
  key: ConsentCategory;
  label: string;
  description: string;
  always?: boolean;
}> = [
  {
    key: "necessary",
    label: "Necesarias",
    description:
      "Imprescindibles para el funcionamiento del sitio y la prestación del servicio (sesión, seguridad, aceptación del propio aviso). No pueden desactivarse.",
    always: true,
  },
  {
    key: "preferences",
    label: "Preferencias",
    description:
      "Permiten recordar opciones como idioma o vistas favoritas para mejorar tu experiencia.",
  },
  {
    key: "analytics",
    label: "Análisis",
    description:
      "Nos ayudan a entender cómo se usa la web (páginas visitadas, tiempos de carga) de forma agregada para mejorarla.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Permiten mostrar contenido embebido de terceros (mapas, vídeos) o medir campañas. Pueden implicar transferencias internacionales.",
  },
];

export type ConsentChoices = Record<ConsentCategory, boolean>;

export interface ConsentState {
  version: number;
  timestamp: number;
  choices: ConsentChoices;
}

const DEFAULT_CHOICES: ConsentChoices = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

const CHANGE_EVENT = "cookie-consent:change";
export const OPEN_PREFS_EVENT = "cookie-consent:open-prefs";

function readState(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (Date.now() - parsed.timestamp > CONSENT_MAX_AGE_MS) return null;
    return {
      version: parsed.version,
      timestamp: parsed.timestamp,
      choices: { ...DEFAULT_CHOICES, ...parsed.choices, necessary: true },
    };
  } catch {
    return null;
  }
}

function writeState(choices: ConsentChoices): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: Date.now(),
    choices: { ...choices, necessary: true },
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    /* noop */
  }
  return state;
}

function clearState() {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    /* noop */
  }
}

function subscribe(cb: () => void) {
  const onChange = () => cb();
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useConsent() {
  const state = useSyncExternalStore(
    subscribe,
    () => {
      const s = readState();
      return s ? JSON.stringify(s) : "";
    },
    () => "",
  );
  const parsed: ConsentState | null = state ? JSON.parse(state) : null;
  return {
    state: parsed,
    hasDecision: parsed !== null,
    acceptAll: () =>
      writeState({ necessary: true, preferences: true, analytics: true, marketing: true }),
    rejectAll: () =>
      writeState({ necessary: true, preferences: false, analytics: false, marketing: false }),
    save: (choices: ConsentChoices) => writeState(choices),
    reset: () => clearState(),
  };
}

export function hasConsent(category: ConsentCategory): boolean {
  if (category === "necessary") return true;
  const s = readState();
  return !!s?.choices[category];
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
}

export function useOpenPreferencesListener(onOpen: () => void) {
  useEffect(() => {
    const handler = () => onOpen();
    window.addEventListener(OPEN_PREFS_EVENT, handler);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, handler);
  }, [onOpen]);
}

// Small helper for components that just want a boolean and reactivity
export function useHasConsent(category: ConsentCategory): boolean {
  const [ok, setOk] = useState(() => hasConsent(category));
  useEffect(() => {
    const update = () => setOk(hasConsent(category));
    window.addEventListener(CHANGE_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(CHANGE_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, [category]);
  return ok;
}